import React, { useState, useEffect, useRef } from 'react';
import { UserPlus, Edit, Trash2, Search, Filter, X, Percent, AlertCircle, Hash } from 'lucide-react';
import { Player } from '../types';
import FloatingActionButton from '../components/FloatingActionButton';
import { getPlayers, createPlayer, updatePlayer, deletePlayer } from '../services/playerService';

const Players: React.FC = () => {
  const [players, setPlayers] = useState<Player[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentPlayer, setCurrentPlayer] = useState<Player | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    squad: '',
    subscriptionStatus: ''
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Form state
  const [formData, setFormData] = useState<Omit<Player, 'id'>>({
    name: '',
    fatherName: '',
    motherName: '',
    fatherPhone: '',
    motherPhone: '',
    fatherEmail: '',
    motherEmail: '',
    dateOfBirth: '',
    address: '',
    squad: 'Elite U11',
    subscriptionStatus: 'Active',
    subscriptionValidTill: '',
    scholarshipPercentage: 0,
    aiffPlayerId: ''
  });

  // Load players on component mount
  useEffect(() => {
    loadPlayers();
  }, []);

  const loadPlayers = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await getPlayers();
      setPlayers(data);
    } catch (err) {
      console.error('Error loading players:', err);
      setError('Failed to load players. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const clearFilters = () => {
    setFilters({
      squad: '',
      subscriptionStatus: ''
    });
  };

  const handleAddPlayer = () => {
    setCurrentPlayer(null);
    setFormData({
      name: '',
      fatherName: '',
      motherName: '',
      fatherPhone: '',
      motherPhone: '',
      fatherEmail: '',
      motherEmail: '',
      dateOfBirth: '',
      address: '',
      squad: 'Elite U11',
      subscriptionStatus: 'Active',
      subscriptionValidTill: '',
      scholarshipPercentage: 0,
      aiffPlayerId: ''
    });
    setIsModalOpen(true);
  };

  const handleEditPlayer = (player: Player) => {
    setCurrentPlayer(player);
    setFormData({
      name: player.name,
      fatherName: player.fatherName,
      motherName: player.motherName,
      fatherPhone: player.fatherPhone,
      motherPhone: player.motherPhone,
      fatherEmail: player.fatherEmail,
      motherEmail: player.motherEmail,
      dateOfBirth: player.dateOfBirth,
      address: player.address,
      squad: player.squad,
      subscriptionStatus: player.subscriptionStatus,
      subscriptionValidTill: player.subscriptionValidTill,
      scholarshipPercentage: player.scholarshipPercentage,
      aiffPlayerId: player.aiffPlayerId || ''
    });
    setIsModalOpen(true);
  };

  const handleDeletePlayer = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this player?')) {
      try {
        await deletePlayer(id);
        setPlayers(players.filter(player => player.id !== id));
      } catch (err) {
        console.error('Error deleting player:', err);
        alert('Failed to delete player. Please try again.');
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (currentPlayer) {
        // Update existing player
        const updatedPlayer = await updatePlayer(currentPlayer.id, formData);
        setPlayers(players.map(player =>
          player.id === currentPlayer.id ? updatedPlayer : player
        ));
      } else {
        // Add new player
        const newPlayer = await createPlayer(formData);
        setPlayers([...players, newPlayer]);
      }

      setIsModalOpen(false);
    } catch (err) {
      console.error('Error saving player:', err);
      alert('Failed to save player. Please try again.');
    }
  };

  const getSubscriptionStatusColor = (status: string) => {
    switch (status) {
      case 'Active':
        return 'bg-green-100 text-green-800';
      case 'Default':
        return 'bg-yellow-100 text-yellow-800';
      case 'Inactive':
        return 'bg-gray-100 text-gray-800';
      case 'Unpaid':
        return 'bg-red-100 text-red-600';
      case 'Trial':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-blue-100 text-blue-800';
    }
  };

  const getScholarshipBadgeColor = (percentage: number) => {
    if (percentage === 0) return 'bg-gray-100 text-gray-800';
    if (percentage <= 25) return 'bg-blue-100 text-blue-800';
    if (percentage <= 50) return 'bg-green-100 text-green-800';
    if (percentage <= 75) return 'bg-yellow-100 text-yellow-800';
    return 'bg-purple-100 text-purple-800';
  };

  const filteredPlayers = players.filter(player => {
    // Apply search filter
    const matchesSearch =
      player.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      player.squad.toLowerCase().includes(searchTerm.toLowerCase()) ||
      player.subscriptionStatus.toLowerCase().includes(searchTerm.toLowerCase()) ||
      player.fatherName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      player.motherName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      player.fatherPhone.toLowerCase().includes(searchTerm.toLowerCase()) ||
      player.motherPhone.toLowerCase().includes(searchTerm.toLowerCase()) ||
      player.fatherEmail.toLowerCase().includes(searchTerm.toLowerCase()) ||
      player.motherEmail.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (player.aiffPlayerId && player.aiffPlayerId.toLowerCase().includes(searchTerm.toLowerCase()));

    // Apply dropdown filters
    const matchesSquad = filters.squad ? player.squad === filters.squad : true;
    const matchesSubscription = filters.subscriptionStatus ? player.subscriptionStatus === filters.subscriptionStatus : true;

    return matchesSearch && matchesSquad && matchesSubscription;
  });

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center">
          <p className="text-gray-500">Loading players...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 border-l-4 border-red-400 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <AlertCircle className="h-5 w-5 text-red-400" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
              <button
                className="mt-2 text-sm font-medium text-red-700 hover:text-red-600"
                onClick={loadPlayers}
              >
                Try again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Players</h1>
        <button
          onClick={handleAddPlayer}
          className="btn-primary flex items-center md:flex hidden"
        >
          <UserPlus size={20} className="mr-2" />
          <span className="hidden sm:inline">Add Player</span>
          <span className="sm:hidden">Add</span>
        </button>
      </div>

      <div className="mb-6 space-y-4">
        <div className="flex items-center justify-between">
          <div className="relative flex-grow mr-4">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search size={20} className="text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search players..."
              className="pl-10 form-input w-full"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center px-4 py-2 border border-gray-300 rounded-md bg-white text-gray-700 hover:bg-gray-50"
          >
            <Filter size={20} className="mr-2" />
            <span className="hidden sm:inline">Filters</span>
          </button>
        </div>

        {showFilters && (
          <div className="bg-gray-50 p-4 rounded-md">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-sm font-medium text-gray-700">Filter Players</h3>
              <button
                onClick={clearFilters}
                className="text-sm text-navy-600 hover:text-navy-800 flex items-center"
              >
                <X size={16} className="mr-1" />
                Clear Filters
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="squad-filter" className="block text-sm font-medium text-gray-700 mb-1">
                  Squad
                </label>
                <select
                  id="squad-filter"
                  name="squad"
                  className="form-input"
                  value={filters.squad}
                  onChange={handleFilterChange}
                >
                  <option value="">All Squads</option>
                  <option value="Elite U11">Elite U11</option>
                  <option value="Elite U13">Elite U13</option>
                  <option value="Elite U15">Elite U15</option>
                  <option value="Elite U17">Elite U17</option>
                  <option value="Regular">Regular</option>
                </select>
              </div>
              <div>
                <label htmlFor="subscription-filter" className="block text-sm font-medium text-gray-700 mb-1">
                  Subscription Status
                </label>
                <select
                  id="subscription-filter"
                  name="subscriptionStatus"
                  className="form-input"
                  value={filters.subscriptionStatus}
                  onChange={handleFilterChange}
                >
                  <option value="">All Statuses</option>
                  <option value="Active">Active</option>
                  <option value="Trial">Trial</option>
                  <option value="Default">Default</option>
                  <option value="Inactive">Inactive</option>
                  <option value="Unpaid">Unpaid</option>
                </select>
              </div>
            </div>
            <div className="mt-3 text-sm text-gray-500">
              Showing {filteredPlayers.length} of {players.length} players
            </div>
          </div>
        )}
      </div>

      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <ul className="divide-y divide-gray-200">
          {filteredPlayers.length > 0 ? (
            filteredPlayers.map((player) => (
              <li key={player.id} className="px-4 py-4 sm:px-6 hover:bg-gray-50">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">{player.name}</h3>
                    <p className="text-sm text-gray-500">
                      Father: {player.fatherName} | Mother: {player.motherName}
                    </p>
                    {player.aiffPlayerId && (
                      <p className="text-sm text-gray-500 flex items-center mt-1">
                        <Hash size={16} className="mr-1" />
                        AIFF ID: {player.aiffPlayerId}
                      </p>
                    )}
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleEditPlayer(player)}
                      className="text-blue-600 hover:text-blue-900 p-1"
                      aria-label="Edit player"
                    >
                      <Edit size={18} />
                    </button>
                    <button
                      onClick={() => handleDeletePlayer(player.id)}
                      className="text-red-600 hover:text-red-900 p-1"
                      aria-label="Delete player"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
                <div className="mt-2 sm:flex sm:justify-between">
                  <div className="sm:flex">
                    <p className="flex items-center text-sm text-gray-500">
                      <span className="font-medium mr-2">Squad:</span>
                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                        {player.squad}
                      </span>
                    </p>
                    <p className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0 sm:ml-6">
                      <span className="font-medium mr-2">Subscription:</span>
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getSubscriptionStatusColor(player.subscriptionStatus)}`}>
                        {player.subscriptionStatus}
                      </span>
                    </p>
                    <p className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0 sm:ml-6">
                      <span className="font-medium mr-2">Scholarship:</span>
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getScholarshipBadgeColor(player.scholarshipPercentage)}`}>
                        {player.scholarshipPercentage}%
                      </span>
                    </p>
                  </div>
                </div>
                <div className="mt-2 grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <p className="text-sm text-gray-500">
                      <span className="font-medium">Father's Contact:</span><br />
                      Phone: {player.fatherPhone}<br />
                      Email: {player.fatherEmail}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">
                      <span className="font-medium">Mother's Contact:</span><br />
                      Phone: {player.motherPhone}<br />
                      Email: {player.motherEmail}
                    </p>
                  </div>
                </div>
              </li>
            ))
          ) : (
            <li className="px-4 py-6 text-center text-gray-500">
              No players found. Try adjusting your search or add a new player.
            </li>
          )}
        </ul>
      </div>

      {/* Mobile Floating Action Button */}
      <FloatingActionButton to="#" onClick={handleAddPlayer} />

      {/* Add/Edit Player Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-auto modal-content mobile-scroll overflow-y-auto">
            <div className="px-6 py-4 border-b">
              <h3 className="text-lg font-medium text-gray-900">
                {currentPlayer ? 'Edit Player' : 'Add New Player'}
              </h3>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="px-6 py-4">
                <div className="grid grid-cols-1 gap-4">
                  <div className="form-group">
                    <label htmlFor="name" className="form-label">
                      Player Name
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      className="form-input"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="aiffPlayerId" className="form-label flex items-center">
                      <Hash size={16} className="mr-1" />
                      AIFF Player ID
                    </label>
                    <input
                      type="text"
                      id="aiffPlayerId"
                      name="aiffPlayerId"
                      className="form-input"
                      value={formData.aiffPlayerId}
                      onChange={handleInputChange}
                      placeholder="Optional"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="form-group">
                      <label htmlFor="fatherName" className="form-label">
                        Father's Name
                      </label>
                      <input
                        type="text"
                        id="fatherName"
                        name="fatherName"
                        className="form-input"
                        value={formData.fatherName}
                        onChange={handleInputChange}
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="motherName" className="form-label">
                        Mother's Name
                      </label>
                      <input
                        type="text"
                        id="motherName"
                        name="motherName"
                        className="form-input"
                        value={formData.motherName}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="form-group">
                      <label htmlFor="fatherPhone" className="form-label">
                        Father's Phone
                      </label>
                      <input
                        type="tel"
                        id="fatherPhone"
                        name="fatherPhone"
                        className="form-input"
                        value={formData.fatherPhone}
                        onChange={handleInputChange}
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="motherPhone" className="form-label">
                        Mother's Phone
                      </label>
                      <input
                        type="tel"
                        id="motherPhone"
                        name="motherPhone"
                        className="form-input"
                        value={formData.motherPhone}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="form-group">
                      <label htmlFor="fatherEmail" className="form-label">
                        Father's Email
                      </label>
                      <input
                        type="email"
                        id="fatherEmail"
                        name="fatherEmail"
                        className="form-input"
                        value={formData.fatherEmail}
                        onChange={handleInputChange}
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="motherEmail" className="form-label">
                        Mother's Email
                      </label>
                      <input
                        type="email"
                        id="motherEmail"
                        name="motherEmail"
                        className="form-input"
                        value={formData.motherEmail}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="dateOfBirth" className="form-label">
                      Date of Birth
                    </label>
                    <input
                      type="date"
                      id="dateOfBirth"
                      name="dateOfBirth"
                      className="form-input"
                      value={formData.dateOfBirth}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="address" className="form-label">
                      Address
                    </label>
                    <textarea
                      id="address"
                      name="address"
                      rows={2}
                      className="form-input"
                      value={formData.address}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="form-group">
                      <label htmlFor="squad" className="form-label">
                        Squad
                      </label>
                      <select
                        id="squad"
                        name="squad"
                        className="form-input"
                        value={formData.squad}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="Elite U11">Elite U11</option>
                        <option value="Elite U13">Elite U13</option>
                        <option value="Elite U15">Elite U15</option>
                        <option value="Elite U17">Elite U17</option>
                        <option value="Regular">Regular</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label htmlFor="subscriptionStatus" className="form-label">
                        Subscription Status
                      </label>
                      <select
                        id="subscriptionStatus"
                        name="subscriptionStatus"
                        className="form-input"
                        value={formData.subscriptionStatus}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="Active">Active</option>
                        <option value="Trial">Trial</option>
                        <option value="Default">Default</option>
                        <option value="Inactive">Inactive</option>
                        <option value="Unpaid">Unpaid</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="form-group">
                      <label htmlFor="subscriptionValidTill" className="form-label">
                        Subscription Valid Till
                      </label>
                      <input
                        type="date"
                        id="subscriptionValidTill"
                        name="subscriptionValidTill"
                        className="form-input"
                        value={formData.subscriptionValidTill}
                        onChange={handleInputChange}
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="scholarshipPercentage" className="form-label flex items-center">
                        Scholarship Percentage
                        <Percent size={16} className="ml-1 text-gray-500" />
                      </label>
                      <select
                        id="scholarshipPercentage"
                        name="scholarshipPercentage"
                        className="form-input"
                        value={formData.scholarshipPercentage}
                        onChange={(e) => setFormData({
                          ...formData,
                          scholarshipPercentage: parseInt(e.target.value)
                        })}
                        required
                      >
                        <option value="0">0% (No Scholarship)</option>
                        <option value="25">25%</option>
                        <option value="50">50%</option>
                        <option value="75">75%</option>
                        <option value="100">100% (Full Scholarship)</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
              <div className="px-6 py-4 bg-gray-50 flex justify-end rounded-b-lg">
                <button
                  type="button"
                  className="btn-outline mr-2"
                  onClick={() => setIsModalOpen(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  {currentPlayer ? 'Update Player' : 'Add Player'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Players;